import React, { useState } from 'react';
import { Button, DataGrid, Popup, Form, TextBox } from 'devextreme-react';
import { Column } from 'devextreme-react/data-grid';
import { SimpleItem } from 'devextreme-react/form';
import { formatTime, formatDate, generateExportData } from '../utils/stopwatchUtils';
import { exportToCSV, exportToExcel, printResults } from '../utils';

const StopwatchPopups = ({
    // Athlete Selector
    showAthleteSelector,
    onHideAthleteSelector,
    athleteData,
    onSelectAthlete,
    
    // Unknown Times
    showUnknownTimePopup,
    onHideUnknownTimePopup,
    unknownTimes,
    grid,
    onAssignTimeToAthlete,
    
    // Export
    showExportPopup,
    onHideExportPopup,
    rows,
    lanes,
    recordedTimes,
    
    // Save Session
    showSaveSessionDialog,
    onHideSaveSessionDialog,
    sessionName,
    onSessionNameChange,
    onSaveSession,
    
    // Load Session
    showLoadSessionDialog,
    onHideLoadSessionDialog,
    savedSessions,
    currentSessionId,
    onLoadSession,
    onDeleteSession
}) => {
    const [customName, setCustomName] = useState('');

    const handleCustomNameSubmit = () => {
        if (customName.trim()) {
            // Erstelle ein temporäres Athleten-Objekt mit dem benutzerdefinierten Namen
            const customAthlete = {
                id: `custom_${Date.now()}`,
                surname: customName.trim(),
                lastname: '',
                groupname: 'Benutzerdefiniert',
                clubname: 'Freier Name'
            };
            onSelectAthlete(customAthlete);
            setCustomName('');
        }
    };

    return (
        <>
            {/* Sportler-Auswahl Popup */}
            <Popup
                visible={showAthleteSelector}
                onHiding={() => {
                    setCustomName('');
                    onHideAthleteSelector();
                }}
                title="Sportler auswählen"
                width={600}
                height={600}
            >
                {/* Freitext-Eingabe für benutzerdefinierten Namen */}
                <div style={{ marginBottom: '20px', padding: '10px', backgroundColor: '#f5f5f5', borderRadius: '5px' }}>
                    <h4 style={{ margin: '0 0 10px 0' }}>Freier Name eingeben:</h4>
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <TextBox
                            value={customName}
                            onValueChanged={(e) => setCustomName(e.value)}
                            placeholder="Namen eingeben..."
                            width="70%"
                            onEnterKey={handleCustomNameSubmit}
                        />
                        <Button
                            text="Hinzufügen"
                            type="default"
                            onClick={handleCustomNameSubmit}
                            disabled={!customName.trim()}
                            width="30%"
                        />
                    </div>
                </div>

                <DataGrid
                    dataSource={athleteData}
                    onRowClick={(e) => onSelectAthlete(e.data)}
                    showBorders
                    height={300}
                >
                    <Column dataField="surname" caption="Vorname" />
                    <Column dataField="lastname" caption="Nachname" />
                    <Column dataField="groupname" caption="Trainingsgruppe" />
                    <Column dataField="clubname" caption="Verein" />
                </DataGrid>
                
                <div style={{ marginTop: '10px' }}>
                    <Button
                        text="Leere Zelle"
                        onClick={() => onSelectAthlete(null)}
                        width="100%"
                    />
                </div>
            </Popup>

            {/* Unbekannte Zeiten Popup */}
            <Popup
                visible={showUnknownTimePopup}
                onHiding={onHideUnknownTimePopup}
                title="Unbekannte Zeiten zuordnen"
                width={800}
                height={600}
            >
                <div className="unknown-times-container">
                    {unknownTimes.map((time, timeIndex) => (
                        <div key={timeIndex} className="unknown-time-item">
                            <div className="time-info">
                                <strong>Zeit: {formatTime(time.time)}</strong>
                                <div>Aufgezeichnet: {time.timestamp.toLocaleTimeString()}</div>
                            </div>
                            
                            <div className="assignment-grid">
                                {grid.map((row, rowIndex) => (
                                    <div key={rowIndex} className="assignment-row">
                                        <div className="row-label">Reihe {rowIndex + 1}:</div>
                                        {row.map((athlete, laneIndex) => (
                                            <Button
                                                key={laneIndex}
                                                text={athlete ? `${athlete.surname} ${athlete.lastname}` : 'Leer'}
                                                disabled={!athlete}
                                                onClick={() => onAssignTimeToAthlete(timeIndex, rowIndex, laneIndex)}
                                                width="150px"
                                            />
                                        ))}
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                    
                    {unknownTimes.length === 0 && (
                        <div className="no-unknown-times">
                            Keine unbekannten Zeiten vorhanden.
                        </div>
                    )}
                </div>
            </Popup>

            {/* Export Popup */}
            <Popup
                visible={showExportPopup}
                onHiding={onHideExportPopup}
                title="Ergebnisse exportieren"
                width={400}
                height={300}
            >
                <div className="export-options">
                    <Button
                        text="Als CSV exportieren"
                        icon="export"
                        onClick={async () => {
                            const exportData = generateExportData(grid, recordedTimes, rows, lanes);
                            await exportToCSV(exportData, 'stoppuhr_ergebnisse');
                            onHideExportPopup();
                        }}
                        width="100%"
                    />
                    
                    <Button
                        text="Als Excel exportieren"
                        icon="exportxlsx"
                        onClick={async () => {
                            const exportData = generateExportData(grid, recordedTimes, rows, lanes);
                            await exportToExcel(exportData, 'stoppuhr_ergebnisse');
                            onHideExportPopup();
                        }}
                        width="100%"
                    />
                    
                    <Button
                        text="Drucken"
                        icon="print"
                        onClick={async () => {
                            const exportData = generateExportData(grid, recordedTimes, rows, lanes);
                            await printResults(exportData);
                            onHideExportPopup();
                        }}
                        width="100%"
                    />
                </div>
            </Popup>

        </>
    );
};

export default StopwatchPopups;
